import { ReactNode, useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { UserDetailsDialog } from "@/components/UserDetailsDialog";
import { TermsAndConditionsDialog } from "@/components/TermsAndConditionsDialog";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { NetworkError } from "@/components/NetworkError";
import { IncompleteSetupAlert } from "@/components/IncompleteSetupAlert";
import { Loader2 } from "lucide-react";
import { categoryApi } from "@/lib/api";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, firebaseUser, loading, acceptTerms } = useUser();
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [isCheckingData, setIsCheckingData] = useState(false);
  const [debugForceOnboarding, setDebugForceOnboarding] = useState(false);
  const [showIncompleteAlert, setShowIncompleteAlert] = useState(false);

  // Check if onboarding should be shown based on existing data
  useEffect(() => {
    const checkOnboardingNeeded = async () => {
      // Skip check if debug mode is forcing onboarding
      if (debugForceOnboarding) {
        return;
      }
      
      if (user && user.profileComplete && !onboardingChecked && !isCheckingData) {
        // First check localStorage
        const onboardingCompleted = localStorage.getItem('onboarding_completed');
        const skippedWithoutCategories = localStorage.getItem('onboarding_skipped_without_categories');
        
        if (onboardingCompleted) {
          // Check if user skipped without categories
          if (skippedWithoutCategories === 'true') {
            // Verify if they still don't have categories
            try {
              const categories = await categoryApi.getAll();
              if (categories.length === 0) {
                setShowIncompleteAlert(true);
              } else {
                // User has since added categories, clear the flag
                localStorage.removeItem('onboarding_skipped_without_categories');
              }
            } catch (error) {
              console.error('Failed to check categories:', error);
              setShowIncompleteAlert(true);
            }
          }
          setOnboardingChecked(true);
          return;
        }
        
        // Check if user already has data (categories)
        setIsCheckingData(true);
        try {
          const categories = await categoryApi.getAll();
          
          // If user already has categories, they don't need onboarding
          // (they've already set up their account)
          if (categories.length > 0) {
            console.log('🔄 User already has categories, skipping onboarding');
            localStorage.setItem('onboarding_completed', 'true');
            setShowOnboarding(false);
          } else {
            // New user - show onboarding
            console.log('🆕 New user detected, showing onboarding');
            setShowOnboarding(true);
          }
        } catch (error) {
          console.error('Failed to check user data for onboarding:', error);
          // On error for new users, show onboarding anyway
          // This handles cases where the backend hasn't fully set up the account yet
          console.log('🆕 API error - assuming new user, showing onboarding');
          setShowOnboarding(true);
        } finally {
          setIsCheckingData(false);
          setOnboardingChecked(true);
        }
      }
    };
    
    checkOnboardingNeeded();
  }, [user, onboardingChecked, isCheckingData, debugForceOnboarding]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setDebugForceOnboarding(false);
  };

  const handleSkipWithoutCategories = () => {
    setShowOnboarding(false);
    setDebugForceOnboarding(false);
    setShowIncompleteAlert(true);
  };
  
  const handleOpenOnboardingFromAlert = () => {
    setShowIncompleteAlert(false);
    setShowOnboarding(true);
  };

  // Show loading while auth state is being determined or checking onboarding data
  if (loading || isCheckingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  // Show network error if there's a network connectivity issue
  if (user && user.networkError) {
    return <NetworkError onRetry={() => window.location.reload()} />;
  }

  // Handle terms acceptance flow
  const handleAcceptTerms = async () => {
    setIsAcceptingTerms(true);
    try {
      await acceptTerms();
    } catch (error) {
      console.error('Failed to accept terms:', error);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  // Show terms dialog if user hasn't accepted terms yet
  if (user && !user.termsAccepted) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-muted/50">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Setting up your account...</p>
          </div>
        </div>
        <TermsAndConditionsDialog 
          open={true} 
          onAccept={handleAcceptTerms}
        />
      </>
    );
  }

  // Show profile completion dialog if profile is not complete
  if (user && !user.profileComplete) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-muted/50">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Setting up your profile...</p>
          </div>
        </div>
        <UserDetailsDialog 
          open={true} 
          onOpenChange={() => {}} // Prevent closing
        />
      </>
    );
  }

  // Show onboarding wizard for new users or when debug mode forces it
  if (showOnboarding || debugForceOnboarding) {
    return (
      <OnboardingWizard 
        onComplete={handleOnboardingComplete} 
        onSkipWithoutCategories={handleSkipWithoutCategories}
      />
    );
  }

  // Debug button to preview onboarding wizard (only in development)
  const handleDebugOnboarding = () => {
    console.log('🧪 Debug: Forcing onboarding wizard to show');
    setDebugForceOnboarding(true);
    setShowOnboarding(true);
  };

  // Render protected content if user is authenticated and profile is complete
  return (
    <>
      {/* Incomplete setup alert banner */}
      {showIncompleteAlert && (
        <IncompleteSetupAlert 
          onDismiss={() => setShowIncompleteAlert(false)}
          onOpenSetup={handleOpenOnboardingFromAlert}
        />
      )}
      {/* Add padding when alert is visible */}
      <div className={showIncompleteAlert ? 'pt-14 sm:pt-12' : ''}>
        {children}
      </div>
      {/* Debug button - only visible in development */}
      {import.meta.env.DEV && (
        <button
          onClick={handleDebugOnboarding}
          className="fixed bottom-4 right-4 z-50 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2 transition-all"
          title="Debug: Show Onboarding Wizard"
        >
          🧪 Debug Onboarding
        </button>
      )}
    </>
  );
} 